import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  FolderOpen, Plus, Search, Upload, File, Video, Link as LinkIcon, 
  Image, FileText, Presentation, Trash2, Download, ExternalLink,
  Filter, RefreshCcw, Copy, CheckCircle, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const resourceTypeIcons = {
  pdf: File,
  document: FileText,
  video: Video,
  link: LinkIcon,
  image: Image,
  slides: Presentation
};

const resourceTypeLabels = {
  pdf: 'PDF',
  document: 'Document',
  video: 'Vidéo',
  link: 'Lien',
  image: 'Image',
  slides: 'Présentation'
};

/** Resolve stored upload paths to the current backend origin (rewrite dead absolute hosts). */
const resolveResourceUrl = (url) => {
  if (!url) return null;
  let path = url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      path = new URL(url).pathname;
    } catch {
      return url;
    }
  }
  if (!path.startsWith('/')) path = `/${path}`;
  if (path.startsWith('/uploads/')) path = `/api${path}`;
  else if (!path.startsWith('/api/')) path = path.startsWith('/api') ? path : `/api${path}`;
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
  return base ? `${base}${path}` : path;
};

const ResourceLibraryPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [courses, setCourses] = useState([]);
  
  // Dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  // Form data
  const [uploadForm, setUploadForm] = useState({
    course_id: '',
    title: '',
    description: '',
    resource_type: 'document',
    file: null
  });
  
  const [linkForm, setLinkForm] = useState({
    course_id: '',
    title: '',
    description: '',
    external_url: ''
  });
  
  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/content/resources');
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      toast.error('Erreur lors du chargement des ressources');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchCourses = useCallback(async () => {
    try {
      const response = await api.get('/content/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchResources();
    fetchCourses();
  }, [fetchResources, fetchCourses]);
  
  const handleFileUpload = async () => {
    if (!uploadForm.file || !uploadForm.course_id || !uploadForm.title) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('course_id', uploadForm.course_id);
    formData.append('title', uploadForm.title);
    formData.append('description', uploadForm.description || '');
    formData.append('resource_type', uploadForm.resource_type);
    
    try {
      await api.post('/content/resources/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Ressource uploadée');
      setShowUploadDialog(false);
      resetUploadForm();
      fetchResources();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
  };
  
  const handleAddLink = async () => {
    if (!linkForm.external_url || !linkForm.course_id || !linkForm.title) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }
    
    try {
      await api.post(`/content/resources?course_id=${linkForm.course_id}`, {
        title: linkForm.title,
        description: linkForm.description,
        resource_type: 'link',
        external_url: linkForm.external_url
      });
      toast.success('Lien ajouté');
      setShowLinkDialog(false);
      resetLinkForm();
      fetchResources();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };
  
  const handleDelete = async (resourceId) => {
    if (!window.confirm('Supprimer cette ressource ?')) return;
    
    try {
      await api.delete(`/content/resources/${resourceId}`);
      toast.success('Ressource supprimée');
      fetchResources();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const copyToClipboard = (url) => {
    const fullUrl = resolveResourceUrl(url);
    if (!fullUrl) return;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(url);
    toast.success('URL copiée');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleOpenResource = async (resource) => {
    // External links open directly
    if (resource.external_url && !resource.file_url) {
      window.open(resource.external_url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!resource.file_url) {
      if (resource.external_url) {
        window.open(resource.external_url, '_blank', 'noopener,noreferrer');
        return;
      }
      toast.error('Aucun fichier associé à cette ressource');
      return;
    }

    // Open the tab synchronously (user gesture) to avoid popup blockers, then
    // stream the authenticated blob into it — avoids public /api/uploads 404s.
    const newTab = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const response = await api.get(`/content/resources/${resource.id}/file`, {
        responseType: 'blob',
      });
      const contentType =
        response.headers['content-type'] ||
        (resource.resource_type === 'video' ? 'video/mp4' : 'application/octet-stream');
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      if (newTab) {
        newTab.location.href = blobUrl;
      } else {
        // Popup was blocked — fall back to same-tab navigation
        window.location.href = blobUrl;
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 120000);
    } catch (error) {
      console.error('Error opening resource:', error);
      if (newTab) newTab.close();
      toast.error(
        error.response?.status === 404
          ? 'Fichier introuvable sur le serveur'
          : 'Impossible d\'ouvrir le fichier'
      );
    }
  };
  
  const resetUploadForm = () => {
    setUploadForm({
      course_id: '',
      title: '',
      description: '',
      resource_type: 'document',
      file: null
    });
  };
  
  const resetLinkForm = () => {
    setLinkForm({
      course_id: '',
      title: '',
      description: '',
      external_url: ''
    });
  };
  
  // Filter resources
  const filteredResources = resources.filter(r => {
    const matchesSearch = r.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || r.resource_type === filterType;
    return matchesSearch && matchesType;
  });
  
  // Group by course
  const resourcesByCourse = filteredResources.reduce((acc, r) => {
    const courseKey = r.course_name || 'Sans cours';
    if (!acc[courseKey]) acc[courseKey] = [];
    acc[courseKey].push(r);
    return acc;
  }, {});
  
  // Stats
  const stats = {
    total: resources.length,
    pdf: resources.filter(r => r.resource_type === 'pdf').length,
    video: resources.filter(r => r.resource_type === 'video').length,
    link: resources.filter(r => r.resource_type === 'link').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              aria-label="Retour au tableau de bord"
              data-testid="resource-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Bibliothèque de Ressources
              </h1>
              <p className="text-sm text-slate-500">
                Gérez vos documents, vidéos et liens pédagogiques
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowLinkDialog(true)}
                disabled={courses.length === 0}
                title={courses.length === 0 ? 'Veuillez créer un cours avant d\'ajouter un lien.' : undefined}
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Ajouter un lien
              </Button>
              <Button
                onClick={() => setShowUploadDialog(true)}
                className="bg-violet-600 hover:bg-violet-700"
                disabled={courses.length === 0}
                title={courses.length === 0 ? 'Veuillez créer un cours avant d\'importer des ressources.' : undefined}
              >
                <Upload className="w-4 h-4 mr-2" />
                Uploader
              </Button>
            </div>
            {courses.length === 0 && (
              <p className="text-xs text-amber-600">Aucun cours disponible. Créez un cours avant d'ajouter des ressources.</p>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <File className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.pdf}</p>
                <p className="text-xs text-slate-500">PDF</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Video className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.video}</p>
                <p className="text-xs text-slate-500">Vidéos</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <LinkIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.link}</p>
                <p className="text-xs text-slate-500">Liens</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="pdf">PDF</SelectItem>
              <SelectItem value="document">Documents</SelectItem>
              <SelectItem value="video">Vidéos</SelectItem>
              <SelectItem value="link">Liens</SelectItem>
              <SelectItem value="image">Images</SelectItem>
              <SelectItem value="slides">Présentations</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={fetchResources}>
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>

        {/* Resources List */}
        {filteredResources.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FolderOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                {searchTerm ? 'Aucune ressource trouvée' : 'Bibliothèque vide'}
              </p>
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Uploader votre première ressource
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(resourcesByCourse).map(([courseName, courseResources]) => (
              <Card key={courseName}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-violet-500" />
                    {courseName}
                    <Badge variant="outline">{courseResources.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {courseResources.map(resource => {
                      const Icon = resourceTypeIcons[resource.resource_type] || FileText;
                      const url = resource.file_url || resource.external_url;
                      
                      return (
                        <div 
                          key={resource.id}
                          className="p-4 border rounded-lg hover:border-violet-200 transition-colors bg-white dark:bg-slate-800"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              resource.resource_type === 'pdf' ? 'bg-red-100' :
                              resource.resource_type === 'video' ? 'bg-blue-100' :
                              resource.resource_type === 'link' ? 'bg-emerald-100' :
                              'bg-slate-100'
                            }`}>
                              <Icon className={`w-5 h-5 ${
                                resource.resource_type === 'pdf' ? 'text-red-600' :
                                resource.resource_type === 'video' ? 'text-blue-600' :
                                resource.resource_type === 'link' ? 'text-emerald-600' :
                                'text-slate-600'
                              }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 dark:text-white truncate">
                                {resource.title}
                              </p>
                              <p className="text-xs text-slate-500">
                                {resourceTypeLabels[resource.resource_type]} 
                                {resource.file_size && ` • ${Math.round(resource.file_size / 1024)} KB`}
                              </p>
                              {resource.description && (
                                <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                                  {resource.description}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex gap-2 mt-3">
                            {url && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="flex-1"
                                  onClick={() => handleOpenResource(resource)}
                                  data-testid={`open-resource-${resource.id}`}
                                >
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  Ouvrir
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => copyToClipboard(url)}
                                >
                                  {copiedId === url ? (
                                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                                  ) : (
                                    <Copy className="w-4 h-4" />
                                  )}
                                </Button>
                              </>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDelete(resource.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Uploader une ressource</DialogTitle>
            <DialogDescription>
              Ajoutez un fichier à votre bibliothèque
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Cours *</Label>
              <Select 
                value={uploadForm.course_id} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, course_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un cours" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map(course => (
                    <SelectItem key={course.id} value={course.id.toString()}>
                      {course.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Titre *</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder="Nom de la ressource"
              />
            </div>
            
            <div>
              <Label>Type</Label>
              <Select 
                value={uploadForm.resource_type} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, resource_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="video">Vidéo</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="slides">Présentation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Fichier *</Label>
              <Input
                type="file"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Description optionnelle"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUploadDialog(false); resetUploadForm(); }}>
              Annuler
            </Button>
            <Button onClick={handleFileUpload} className="bg-violet-600 hover:bg-violet-700">
              <Upload className="w-4 h-4 mr-2" />
              Uploader
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un lien</DialogTitle>
            <DialogDescription>
              Ajoutez une ressource externe à votre bibliothèque
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Cours *</Label>
              <Select 
                value={linkForm.course_id} 
                onValueChange={(v) => setLinkForm({ ...linkForm, course_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un cours" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map(course => (
                    <SelectItem key={course.id} value={course.id.toString()}>
                      {course.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Titre *</Label>
              <Input
                value={linkForm.title}
                onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
                placeholder="Nom de la ressource"
              />
            </div>
            
            <div>
              <Label>URL *</Label>
              <Input
                value={linkForm.external_url}
                onChange={(e) => setLinkForm({ ...linkForm, external_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={linkForm.description}
                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
                placeholder="Description optionnelle"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowLinkDialog(false); resetLinkForm(); }}>
              Annuler
            </Button>
            <Button onClick={handleAddLink} className="bg-violet-600 hover:bg-violet-700">
              <LinkIcon className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceLibraryPage;
